import { useState } from "react"
import { ChevronDown, Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

interface ClientFilterComboboxProps {
  clients: string[]
  selected: string[]
  onToggle: (client: string) => void
  onClear: () => void
}

export function ClientFilterCombobox({
  clients,
  selected,
  onToggle,
  onClear,
}: ClientFilterComboboxProps) {
  const [search, setSearch] = useState("")
  const filtered = search
    ? clients.filter((c) => c.toLowerCase().includes(search.toLowerCase()))
    : clients

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-8 gap-1 text-xs", selected.length > 0 && "border-foreground/30")}
        >
          Client
          {selected.length > 0 && (
            <span className="ml-0.5 rounded-full bg-primary text-primary-foreground text-[10px] h-4 min-w-4 px-1 inline-flex items-center justify-center">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        <div className="max-h-52 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="text-meta text-center py-4">No clients found</p>
          ) : (
            filtered.map((client) => {
              const isSelected = selected.includes(client)
              return (
                <button
                  key={client}
                  onClick={() => onToggle(client)}
                  className={cn(
                    "flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-xs text-left hover:bg-accent",
                    isSelected && "bg-accent"
                  )}
                >
                  <div
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                      isSelected
                        ? "bg-[hsl(var(--brand))] border-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]"
                        : "border-muted-foreground/30"
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="truncate">{client}</span>
                </button>
              )
            })
          )}
        </div>
        {selected.length > 0 && (
          <div className="p-1 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-meta h-7"
              onClick={onClear}
            >
              Clear selection
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
